import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { b as EGDSLineSkeleton } from '../skeleton-line/index-chunk.esm.js';
import { c as classNames } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-date-range.css';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';
const EGDSDateRangeButtonTextThemeMap = [['positive', 'positive'], ['link', 'link']];
const EGDSDateRangeButtonTextThemes = new Map(EGDSDateRangeButtonTextThemeMap);
const applyFade = target => {
  const maxScrollLeft = target.scrollWidth - target.clientWidth;
  const targetAbsoluteScroll = Math.abs(target.scrollLeft);
  if (targetAbsoluteScroll >= maxScrollLeft - 10) {
    // @ts-ignore
    target.classList.add(...['unfade-end']);
    target.classList.remove(...['fade-end']);
  } else {
    target.classList.add(...['fade-end']);
    target.classList.remove(...['unfade-end']);
  }
  if (targetAbsoluteScroll <= 10) {
    target.classList.remove(...['fade-start']);
    target.classList.remove(...['fade-end']);
  } else {
    target.classList.add(...['fade-start']);
  }
};
const getClassNameList$1 = classNames.bind(styles);
const EGDSDateRange = ({
  loading,
  loadingMessage,
  results
}) => {
  const baseClassName = 'uitk-date-range';
  const buttonClassName = `${baseClassName}-button`;
  const scrollMouse = {
    move: false,
    initialMousePos: 0,
    buttonLeft: 0
  };
  const dateRangeRef = useRef(null);
  const getSelectedDateResultsIndex = () => results.findIndex(resultObj => resultObj.selected);
  const selectedDateIndex = getSelectedDateResultsIndex();
  const buttonScroll = parent => {
    if (parent.currentTarget) {
      applyFade(parent.currentTarget);
    }
  };
  let resizeTimeout;
  const mouseDown = ev => {
    scrollMouse.move = true;
    scrollMouse.initialMousePos = ev.clientX;
    scrollMouse.buttonLeft = ev.currentTarget.scrollLeft;
  };
  const mouseUp = () => {
    scrollMouse.move = false;
  };
  const mouseMove = ev => {
    if (scrollMouse.move) {
      // eslint-disable-next-line no-param-reassign
      ev.currentTarget.scrollLeft = scrollMouse.buttonLeft + scrollMouse.initialMousePos - ev.clientX;
      buttonScroll(ev);
    }
  };
  const onWindowResize = () => {
    if (!resizeTimeout) {
      resizeTimeout = setTimeout(() => {
        const dateRangeEl = dateRangeRef && dateRangeRef.current;
        const selectedDateScrollCenterDistance = EGDSDateRangeGetSelectedDateScrollCenterDistance(dateRangeEl, selectedDateIndex);
        dateRangeEl && (dateRangeEl.scrollLeft = selectedDateScrollCenterDistance);
        resizeTimeout = null;
        applyFade(dateRangeRef.current);
      }, 200);
    }
  };
  useEffect(() => {
    const dateRangeEl = dateRangeRef && dateRangeRef.current;
    const selectedDateScrollCenterDistance = EGDSDateRangeGetSelectedDateScrollCenterDistance(dateRangeEl, selectedDateIndex);
    dateRangeEl && (dateRangeEl.scrollLeft = selectedDateScrollCenterDistance);
    applyFade(dateRangeRef.current);
    window.addEventListener('resize', onWindowResize);
    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);
  return jsxs("div", {
    className: getClassNameList$1(`${baseClassName}-container`),
    children: [loading && loadingMessage && jsx("span", {
      className: "is-visually-hidden",
      children: loadingMessage
    }), jsxs("ul", {
      ref: dateRangeRef,
      role: "tablist",
      className: getClassNameList$1(baseClassName),
      onScroll: buttonScroll,
      onMouseDown: mouseDown,
      onMouseUp: mouseUp,
      onMouseMove: mouseMove,
      onMouseLeave: mouseUp,
      children: [!loading && results.map((result, index) => {
        const buttonName = `${buttonClassName}-${index}`;
        return jsx("li", {
          className: getClassNameList$1(`${baseClassName}-button-wrapper-natural`),
          role: "presentation",
          children: jsx(EGDSDateRangeButton, {
            className: getClassNameList$1(buttonClassName),
            result: result
          })
        }, buttonName);
      }), loading && [...Array(7)].map((_, index) => jsx("li", {
        className: getClassNameList$1('uitk-date-range-loading-cell'),
        role: "tab",
        children: jsx(EGDSLineSkeleton, {})
      }, `uitk-date-range-loading-cell-${index}`))]
    })]
  });
};
const getClassNameList = classNames.bind(styles);
const EGDSDateRangeButton = ({
  className,
  result
}) => {
  const {
    accessibilityMessage,
    analytics,
    dateText,
    onClickCallback,
    selected = false,
    text,
    textTheme
  } = result;
  const onClickCapture = () => {
    analytics && analyticsTracker(analytics);
    onClickCallback && onClickCallback();
  };
  const buttonClassNames = getClassNameList(className, {
    // eslint-disable-next-line no-useless-computed-key
    ['selected']: selected,
    [`${textTheme}`]: textTheme
  });
  const buttonTextClass = getClassNameList(`uitk-date-range-button-text`);
  const buttonDateTextClass = getClassNameList(`uitk-date-range-button-date-text`);
  return jsxs("button", {
    type: "button",
    "aria-selected": selected,
    "aria-label": accessibilityMessage,
    className: buttonClassNames,
    onClickCapture: onClickCapture,
    role: "tab",
    children: [jsx("span", {
      className: buttonDateTextClass,
      children: dateText
    }), jsx("span", {
      className: buttonTextClass,
      children: text
    })]
  });
};
const EGDSDateRangeGetCenterScrollDistance = (selectedDateOffset, selectedDateWidth, dateRangeWidth) => selectedDateOffset + selectedDateWidth / 2 - dateRangeWidth / 2 > 0 ? selectedDateOffset + selectedDateWidth / 2 - dateRangeWidth / 2 : 0;
const EGDSDateRangeGetSelectedDateScrollCenterDistance = (dateRangeEl, selectedDateIndex) => {
  if (!dateRangeEl) {
    return 0;
  }
  const selectedDateEl = dateRangeEl.querySelectorAll('li')[selectedDateIndex];
  const dateRangePxWidth = dateRangeEl.offsetWidth;
  const selectedDatePxWidth = selectedDateEl && selectedDateEl.offsetWidth;
  const selectedDatePxOffset = selectedDateEl && selectedDateEl.offsetLeft;
  return EGDSDateRangeGetCenterScrollDistance(selectedDatePxOffset, selectedDatePxWidth, dateRangePxWidth);
};
export { EGDSDateRangeButtonTextThemes as E, EGDSDateRange as a, EGDSDateRangeButton as b, EGDSDateRangeGetCenterScrollDistance as c, EGDSDateRangeGetSelectedDateScrollCenterDistance as d, applyFade as e };